import React, { FunctionComponent, useRef } from 'react';
import withStyles from 'react-jss';
import { Grid, Header } from 'semantic-ui-react';
import { format } from 'd3-format';
import moment from 'moment';
import {
  map,
  addIndex,
  pathOr,
  isEmpty,
  mapObjIndexed,
  values,
  omit,
} from 'ramda';
import {
  ResponsiveContainer,
  CartesianGrid,
  YAxis,
  XAxis,
  Tooltip,
  LineChart,
  Line,
} from 'recharts';

import { ClassProp } from 'helpers/types';
import DownloadWidget from 'components/DownloadWidget';
import Legend from 'components/Legend';
import { CSVParams } from 'scenes/CSVExport/CSVExport.helper';
import TickFormatter from 'components/TickFormatter';
import CustomTooltip from 'components/CustomTooltip';
import { Field } from 'components/CustomTooltip/CustomTooltip';

import {
  Transactions,
  TransactionEvent,
  CHART_LEGEND,
  ChartLegend,
} from './TransactionsChart.helpers';
import styles from './TransactionsChart.styles';

type Props = {
  title?: string;
  legend?: ChartLegend[];
  index?: string;
  header?: any;
  tooltip?: boolean;
  tickData?: Record<string, unknown>[];
  transactions: Transactions;
  classes: ClassProp<typeof styles>;
  parameters: CSVParams;
};

const INTERVAL_LIMIT = 31;
const mapIndexed = addIndex(map);

const makeTooltipFields = mapObjIndexed(
  (_: TransactionEvent[], name: string) => ({
    name,
    label: name,
    value: 'count',
    formatter: format('.0f'),
  }),
);

const formatData = map(({ day, ...rest }: any) => ({
  day: moment(day, 'YYYY-MM-DD').valueOf(),
  ...rest,
}));

const renderLineSeries = (transactions: Transactions) => ({
  type,
  color,
}: any) => {
  const data: TransactionEvent[] = pathOr([], type, transactions);
  const name = type.join(' ');

  return (
    <Line
      dataKey="count"
      data={formatData(data)}
      name={name}
      key={name}
      fill={color}
      stroke={color}
      dot={false}
      strokeWidth={2}
    />
  );
};

const getTickData = (transactions: Transactions) => {
  const [tickData] = values(transactions);
  return tickData;
};

const TransactionsChart: FunctionComponent<Props> = ({
  legend = CHART_LEGEND,
  header,
  transactions,
  parameters,
  classes,
  tooltip = true,
  tickData = getTickData(transactions) || 0,
}) => {
  const tooltipFields = values(
    makeTooltipFields(omit(['__typename'])(transactions)),
  ) as Field[];

  const interval = tickData.length > INTERVAL_LIMIT ? 'preserveStartEnd' : 0;
  const contentRef = useRef(null);

  return (
    <Grid className={classes.grid}>
      <DownloadWidget
        nodeRef={contentRef}
        csvParams={parameters}
        header={header}
        imageName="TransactionChart"
      />

      <div ref={contentRef} className={classes.ref}>
        <Grid.Row className={classes.chartContent}>
          <Grid.Column
            width={16}
            columns={1}
            textAlign="center"
            verticalAlign="middle"
          >
            <Legend legend={legend} />
            <Grid.Column>
              {isEmpty(transactions) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.charts}>
                  <ResponsiveContainer>
                    <LineChart>
                      <CartesianGrid vertical={false} />
                      <YAxis dataKey="count" />

                      <XAxis
                        type="number"
                        dataKey="day"
                        scale="time"
                        interval={interval}
                        domain={['auto', 'auto']}
                        allowDuplicatedCategory={false}
                        tick={<TickFormatter xAxis="day" data={tickData} />}
                      />

                      {tooltip && (
                        <Tooltip
                          content={
                            <CustomTooltip
                              fields={tooltipFields}
                              xAxis={{
                                label: 'Day',
                                key: 'day',
                                type: 'date',
                              }}
                            />
                          }
                          cursor={{
                            fill: '#0000000f',
                          }}
                        />
                      )}

                      {mapIndexed(renderLineSeries(transactions), legend)}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>
        </Grid.Row>
      </div>
    </Grid>
  );
};

export default withStyles(styles)(TransactionsChart);
